import { createSlice } from "@reduxjs/toolkit";
import apiService from "@utils/apiService";
import { Constants } from "@constants/Constants";
import { setInitialState, updateFilter } from "./filter.slice";
import { setUseCaseIsSaved, setUseCaseSaveOrRemoveFailure } from "./saveToScorecard.slice";

const initialState = {
  useCases: [],
  useCasesLoading: false,
  suggestions: [],
  emptySuggestions: false,
  suggestionError: false,
  rowsPerPage: 20,
  currentPage: 0,
  totalElements: 0,
  totalSavedElements: 0,
  totalNotViewedElements: 0,
  usecase: {},
  loading: false,
  loadingMore: false,
  error: false,
  useCasesDetailError: false,
  useCasesDetailErrorCode: null,
  useCaseId: '',
  suggestionLoading: false,
  printingUseCase: false,
  selectedChip: 'ALL',
  sortingOptions: [],
  sortingValue: Constants.USE_CASE_SORTING.DEFUALT_OPTION,
};

function convertSearchPayload(payload){
  return {
    businessFunctions: payload.businessFunctions,
    subFunctions: payload.subFunctions,
    industries: payload.industries,
    subIndustries: payload.subIndustries,
    implementationGuide: payload.implementationGuide,
    businessValues: payload.businessValues,
    businessValueRatings: payload.businessValueRatings,
    feasibilityRatings: payload.feasibilityRatings,
    useCaseCategories: payload.useCaseCategories,
    zodiacCategory: payload.zodiacCategory,
    token: payload.token,
    pageNum: payload.page,
    pageSize: payload.size,
    sortingValue: payload.sortingValue,
    tab: payload.tab,
  }
}

function getSearchFilterBody(filter) {
  const filterArray = []
  if (filter.businessFunctions.length) {
    filterArray.push({
      label: "BUSINESS_FUNCTIONS",
      filterValues: filter.businessFunctions,
    });
  }
  if (filter.subFunctions.length) {
    filterArray.push({
      label: "SUB_FUNCTION",
      filterValues: filter.subFunctions,
    });
  }
  if (filter.industries.length) {
    filterArray.push({
      label: "INDUSTRY",
      filterValues: filter.industries,
    });
  }
  if (filter.subIndustries.length) {
    filterArray.push({
      label: "SUB_INDUSTRY",
      filterValues: filter.subIndustries,
    });
  }
  if (filter.implementationGuide.length) {
    filterArray.push({
      label: "IMPLEMENTATION_GUIDE",
      filterValues: filter.implementationGuide,
    });
  }
  if (filter.businessValues.length) {
    filterArray.push({
      label: "BUSINESS_VALUES",
      filterValues: filter.businessValues,
    });
  }
  if (filter.businessValueRatings.length) {
    filterArray.push({
      label: "BUSINESS_VALUE_RATING",
      filterValues: filter.businessValueRatings,
    });
  }
  if (filter.feasibilityRatings.length) {
    filterArray.push({
      label: "FEASIBILITY_RATING",
      filterValues: filter.feasibilityRatings,
    });
  }
  if (filter.useCaseCategories.length) {
    filterArray.push({
      label: "USE_CASE_CATEGORY",
      filterValues: filter.useCaseCategories,
    });
  }
  if (filter.zodiacCategory.length) {
    filterArray.push({
      label: "ZODIAC_CATEGORY",
      filterValues: filter.zodiacCategory,
    });
  }
  return filterArray;
}

export const useCaseSlice = createSlice({
  name: "usecases",
  initialState,
  reducers: {
    updateUseCasesLoading: (state, action) => {
      state.useCasesLoading = action.payload;
    },
    updateUseCases: (state, action) => {
      state.useCases = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setTotalElements: (state, action) => {
      state.totalElements = action.payload;
    },
    setTotalSavedElements: (state, action) => {
      state.totalSavedElements = action.payload;
    },
    setTotalNotViewedElements: (state, action) => {
      state.totalNotViewedElements = action.payload;
    },
    setSelectedChip: (state, action) => {
      state.selectedChip = action.payload;
    },
    setLoadingMore: (state, action) => {
      state.loadingMore = action.payload;
    },
    setUseCaseDetail: (state, action) => {
      state.usecase = action.payload;
      state.loading = false;
      state.useCasesDetailError = false
      state.useCasesDetailErrorCode = null;
    },
    updateUseCaseDetail: (state, action) => {
      state.usecase = action.payload;
    },
    useCaseDetailFailure: (state, action) => {
      state.loading = false;
      state.useCasesDetailError = true
      state.useCasesDetailErrorCode = action.payload
    },
    useCaseFailure: (state) => {
      state.error = true;
      state.loading = false;
    },
    resetData: (state, action) => {
      state.usecase = {};
      state.error = false;
      state.loading = false;
      state.useCasesDetailError = false
      state.useCasesDetailErrorCode = null;
    },
    startLoader: (state) => {
      state.loading = true;
    },
    setUseCaseId: (state, action) => {
      state.useCaseId = action.payload;
    },
    setSuggestionLoading: (state, action) => {
      state.suggestionLoading = action.payload;
    },
    updateSuggestions: (state, action) => {
      state.suggestions = action.payload;
    },
    updateEmptySuggestions: (state, action) => {
      state.emptySuggestions = action.payload;
    },
    updateSuggestionError: (state, action) => {
      state.suggestionError = action.payload;
    },
    setPrintingUseCase: (state, action) => {
      state.printingUseCase = action.payload;
    },
    setSortingOptions: (state, action) => {
      state.sortingOptions = action.payload;
    },
    updateSortingValue: (state, action) => {
      state.sortingValue = action.payload;
    },
  },
});
const { actions, reducer } = useCaseSlice;
export const { 
  setLoadingMore,
  updateUseCasesLoading,
  updateUseCases, 
  setCurrentPage, 
  setRowsPerPage,
  setTotalElements,
  setTotalSavedElements,
  setTotalNotViewedElements,
  setSelectedChip,
  setUseCaseDetail, 
  updateUseCaseDetail,
  useCaseDetailFailure, 
  resetData,
  startLoader, 
  useCaseFailure, 
  setUseCaseId,
  updateSuggestions,
  updateEmptySuggestions,
  updateSuggestionError,
  setSuggestionLoading,
  setPrintingUseCase,
  setSortingOptions,
  updateSortingValue,
} = actions;

export const getUseCases = (data) => async (dispatch, getState) => {
  const isFirstFilter = [...data.industries, ...data.subIndustries, ...data.businessFunctions, ...data.businessValues].length === 1;
  if (isFirstFilter && !data.defaultFilter?.length) {
    let defaultFilter = null;
    if (data.businessFunctions.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.BUSINESS_FUNCTION;
    }
    if (data.subFunctions.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.SUB_FUNCTION;
    }
    if (data.industries.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.INDUSTRY;
    }
    if (data.subIndustries.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.SUB_INDUSTRY;
    }
    if (data.implementationGuide.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.IMPLEMENTATION_GUIDE;
    }
    if (data.businessValues.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.BUSINESS_VALUE;
    }
    if (data.businessValueRatings.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.BUSINESS_VALUE_RATING;
    }
    if (data.feasibilityRatings.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.FEASIBILITY_RATING;
    }
    if (data.useCaseCategories.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.USE_CASE_CATEGORY;
    }
    if (data.zodiacCategory.length) {
      defaultFilter = Constants.SEARCH_FILTER_ENUM.ZODIAC_CATEGORY;
    }
    data.defaultFilter = defaultFilter;
  }
  dispatch(updateUseCasesLoading(true));
  dispatch(updateFilter(data));
  dispatch(setCurrentPage(0));
  dispatch(setRowsPerPage(20));
  const state = getState();
  const filterData = state.filter.selectedValues;
  dispatch(updateFilterData(filterData, data));
  const apiPath = `${Constants.API.USE_CASE_SEMANTIC_SEARCH}/${filterData.searchType || Constants.SEARCH_TYPE.ALL}`;
  const payloadData = {
    ...data,
    implementationGuide: data.implementationGuide?.length ? data.implementationGuide[0] : ""
  }
  apiService.post(apiPath, convertSearchPayload(payloadData))
    .then((response) => {
      if (!Object.keys(response.data?.data).length) {
        dispatch(useCaseFailure());
        return;
      }
      dispatch(updateUseCases(response.data?.data.useCaseList))
      dispatch(setTotalElements(response.data?.data.totalUseCases))
      dispatch(setTotalSavedElements(response.data?.data.totalSavedUseCases))
      dispatch(setTotalNotViewedElements(response.data?.data.totalNotViewedUseCases))
      dispatch(setSortingOptions(response.data?.data?.sortingOptions))
      dispatch(updateSortingValue(response.data?.data?.sortingOptions?.filter(option => option.isSelected)?.value))
      dispatch(updateUseCasesLoading(false));
    })  
    .catch(() => {
      dispatch(useCaseFailure());
    })
}

export const updateFilterData = (filterData, data) => async (dispatch) => {
  const apiPath = `${Constants.API.USE_CASE_SEMANTIC_FILTER}/${filterData.searchType || Constants.SEARCH_TYPE.ALL}`;
  apiService.post(apiPath,
    getSearchFilterBody(filterData),
    {
      params: {
        token: data.token,
        tab: data.tab,
      },
    }
  ).then((response) => {
    dispatch(setInitialState(response.data?.data))
  }).catch(() => {
    dispatch(useCaseFailure());
  })
}

export const resetFilters = () => (dispatch, getState) => {
  const state = getState();
  const newState = { ...state.filter.selectedValues };
  newState.searchType = Constants.SEARCH_TYPE.ALL;
  newState.token = '';
  Constants.FILTERSECTION.USE_CASES_FIELDS.forEach((field) => {
    newState[field] = []
  });
  dispatch(getUseCases(newState))
}

export const loadMore = () => (dispatch, getState) => {
  const state = getState();
  const currentPage = state.usecases.currentPage;
  const currentUseCases = state.usecases.useCases;
  const filterData = state.filter.selectedValues;
  dispatch(setLoadingMore(true));
  dispatch(setCurrentPage(currentPage + 1));
  const apiPath = `${Constants.API.USE_CASE_SEMANTIC_SEARCH}/${filterData.searchType || Constants.SEARCH_TYPE.ALL}`;
  apiService.post(apiPath, convertSearchPayload({
    ...filterData,
    page: currentPage + 1,
    implementationGuide: filterData.implementationGuide?.length ? filterData.implementationGuide[0] : "",
  }))
    .then((response) => {
      dispatch(updateUseCases([
        ...currentUseCases,
        ...(response.data?.data?.useCaseList || [])
      ]))
    }).catch(() => {
      dispatch(setUseCaseSaveOrRemoveFailure({ value: true }));
    }).finally(() => {
      dispatch(setLoadingMore(false));
    })
}

export const getSuggestions = (token) => (dispatch) => {
  dispatch(updateSuggestionError(false));
  dispatch(setSuggestionLoading(true));
  apiService.get(`${Constants.API.SEARCH_SUGGESTION}`, {
    params: {
      token
    },
  })
    .then((response) => {
      dispatch(updateSuggestions(response.data?.data || []));
      dispatch(updateEmptySuggestions(!response.data?.data?.length))
    })
    .catch(() => {
      dispatch(updateSuggestionError(true));
    })
    .finally(() => {
      dispatch(setSuggestionLoading(false))
    })
}

export const getUseCaseDetail = (data) => (dispatch) => {
  dispatch(startLoader());
  dispatch(setUseCaseId(data));
  apiService.get(`${Constants.API.USE_CASE_DETAIL}/${data}`)
    .then((response) => {
      dispatch(setUseCaseDetail(response.data.data));
      dispatch(setUseCaseIsSaved(response?.data?.data?.isSaved));
    }).catch((err) => {
      dispatch(useCaseDetailFailure(err?.response?.status || "error"))
    })
}

export default reducer;
